import React, { Component } from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { MessageBox } from '@common/components/MessageBox';
import Tree from '@common/components/Tree';
import { Search } from '@common/components/Search';
import { Flex } from '@common/components/Flex';
import { Ellipsis } from '@common/components/Ellipsis';
import { ErrorIcon } from '@common/components/error-icon';
import { getTestAttributes } from '@common/utils/tests';
import { Button } from '@common/components/Button';

import './FieldSelector.styles.scss';

export class FieldSelector extends Component {
    static propTypes = {
        close: PropTypes.func.isRequired,
        getFolders: PropTypes.func.isRequired,
        getFields: PropTypes.func.isRequired,
        withSearch: PropTypes.bool,
        closeOnChange: PropTypes.bool,
        multiple: PropTypes.bool,
    };

    static defaultProps = {
        fields: [],
        folders: [],
        closeOnChange: true,
        multiple: false,
    };

    state = {
        selectedFolder: null,
        selectedFields: [],
        folders: [],
        fields: [],
        searchKeyword: '',
        submitting: false,
        lastSelectedIndex: null,
        messageProps: {
            header: '',
            text: '',
            show: false,
            confirmText: 'Ok',
            cancelText: '',
            size: 'medium',
            onCustomOk: () => {},
        },
    };

    constructor(props) {
        super(props);

        this.getFolders();
    }

    getFolders = () => {
        const { getFolders } = this.props;

        getFolders().then((folders) => {
            this.setState((state) => ({ ...state, folders }));
        });
    }

    getFields = async (queries) => {
        let searchKeyword = this.state.searchKeyword;

        if (queries) {
            searchKeyword = queries.searchKeyword;
            this.setState((state) => ({ ...state, searchKeyword: queries.searchKeyword }))
        }

        const { getFields } = this.props;
        const { selectedFolder } = this.state;

        const fields = await getFields({ 
            searchKeyword, 
            folderId: selectedFolder ? selectedFolder.id : '', 
            system: selectedFolder ? selectedFolder.system : false, 
            context: selectedFolder?.context,
        });
        
        this.setState((state) => ({ ...state, fields }));
    }

    handleFolderChange = (id, folder) => {
        const { selectedFolder } = this.state;

        if (id == selectedFolder && selectedFolder.id) {
            return;
        }

        this.setState(
            (state) => ({
                ...state,
                searchKeyword: '',
                selectedFolder: folder,
                selectedFields: [],
            }),
            this.getFields
        );
    }

    handleFieldChange = (e) => {
        e.persist();

        const alreadySelectedItems = this.state.selectedFields;

        const { id } = e.currentTarget.dataset;
        const { fields, selectedFields, lastSelectedIndex } = this.state;
        const { multiple } = this.props;

        const selectedField = fields.find((field) => field.fieldId == id);
        const selectedFieldIndex = fields.findIndex((field) => field.fieldId == id);

        const isRangeSelect = multiple && e.shiftKey && lastSelectedIndex !== null;
        const isMultiSelect = multiple && (e.ctrlKey || e.metaKey);

        if (isMultiSelect) {
            const isSelected = selectedFields.map((field) => field.fieldId).includes(selectedField.fieldId);

            this.setState((state) => ({
                ...state,
                selectedFields: isSelected ? selectedFields.filter((field) => field.fieldId != selectedField.fieldId) : [...selectedFields, selectedField]
            }));

            return;
        }

        if (isRangeSelect) {
            const start = Math.min(lastSelectedIndex, selectedFieldIndex);
            const end = Math.max(lastSelectedIndex, selectedFieldIndex);

            for (let i = start; i <= end; i++) {
                if (!selectedFields.some(field => field.fieldId == fields[i].fieldId)) {
                    selectedFields.push(fields[i]);
                }
            }
        
            this.setState((state) => ({
                ...state,
                lastSelectedIndex: null,
                selectedFields: selectedFields,
            }));

            return
        }

        this.setState((state) => ({
            ...state,
            lastSelectedIndex: multiple ? selectedFieldIndex : null,
            selectedFields: [selectedField],
        }));
    };

    onChange = async () => {
        const { onChange, close, closeOnChange = true, multiple } = this.props;
        const { selectedFields, selectedFolder } = this.state;

        const fieldNormalizer = (field) => ({
            fieldId: field.fieldId,
            fieldName: field.fieldName,
            fieldData: field.fieldData,
            fieldContext: field.fieldContext,
            fieldTypeId: field.fieldTypeId,
            fieldTypeName: field.fieldTypeName,
            systemField: field.systemField,
            columnWidth: field.columnWidth,
            imageURL: field.imageURL,
            options: field.options,
        })

        const fields = selectedFields.map(fieldNormalizer);

        const data = {
          [false]: () => ({
            field: fields[0],
          }),
          [true]: () => ({ fields }),
        };

        try {
            this.setState((x) => ({ ...x, submitting: true }));
            await onChange({
                ...data[multiple](),
                folder: selectedFolder
            });

            this.setState((x) => ({
                 ...x,
                submitting: false, 
                selectedFolder: null,
                fields: [],
                searchKeyword: '',
                selectedFields: [],
            }));

            if (closeOnChange) {
                close();
            }
        } catch (err) {
            this.setState((x) => ({ ...x, submitting: false }));
            this.showErrorMessage(err?.entity || I18n.t('Operation Cancelled'));
            console.error(err);
        }
    }

    setMessageProps = (props) => {
        this.setState((state) => ({
          ...state,
          messageProps: {
            ...state.messageProps,
            size: 'medium',
            confirmText: 'Ok',
            cancelText: '',
            size: 'small',
            ...props,
          },
        }));
    }

    showMessage = (config) => {
        this.setMessageProps({
          ...config,
          show: true,
          onCustomOk: this.closeMessage,
        });
    } 

    closeMessage = () => {
        this.setMessageProps({ show: false });
    }

    showErrorMessage = (message) => {
        this.showMessage({
          header: I18n.t('Operation canceled'),
          content: <Flex style={{ overflow: 'auto' }} alignItems="center"><ErrorIcon className="FieldSelector__error-message-icon" /> {message}</Flex>,
          size: 'small'
          // TODO refactor with ErrorMessage component
        });
    }

    isSelected = (field) => {
        const { multiple } = this.props;
        const { selectedFields } = this.state;

        return selectedFields.some(({ fieldId }) => fieldId == field.fieldId);
    };

    render() {
        const { close, withSearch, getId, title, multiple } = this.props;
        const { selectedFolder, selectedFields, fields, folders, submitting, messageProps, searchKeyword } = this.state;

        return (
            <Modal 
                show
                backdrop="static"
                bsClass="bootstrap modal"
                className="FieldSelector"
                dialogClassName="FieldSelector__dialog"
                animation={false}
                onEscapeKeyDown={close}
                onHide={close}
            >
                <MessageBox
                    hideMessageBox={this.closeMessage}
                    {...messageProps}
                />
                <ModalHeader closeButton onHide={close} className="FieldSelector__dialog__header">
                    <ModalTitle {...getTestAttributes({ id: getId?.title || getTestAttributes('FieldSelector', 'modal', 'title').id })}>{title || I18n.t('Field Chooser')}</ModalTitle>
                </ModalHeader>
                <div className={cx('FieldSelector__content', { 'FieldSelector__content--with-search': withSearch })}>
                    {withSearch && (
                        <Search 
                            iconProps={getTestAttributes({ id: getId?.searchIcon })}
                            id={getId?.search}
                            withLiveSearch
                            value={searchKeyword}
                            placeholder={I18n.t('Search for Fields')}
                            className="FieldSelector__fields-search"
                            onSearch={this.getFields}
                        />
                    )}
                    <Flex width="100%" className="FieldSelector__content__folders-fields">
                        <Tree
                            getId={{
                                folder: getId?.folder,
                                collapseIcon: getId?.folderCollapseIcon,
                                icon: getId?.folderIcon,
                            }}
                            className="FieldSelector__content__folders"
                            items={folders}
                            value={selectedFolder ? selectedFolder.id : undefined} 
                            onChange={this.handleFolderChange} 
                        />
                        <div className="FieldSelector__content__fields">
                            {fields.map((field, rowIndex) => (
                                <button     
                                    title={field.fieldName}
                                    key={field.fieldId} 
                                    data-id={field.fieldId}
                                    id={getId?.field ? getId.field(field, rowIndex) : `field-id-${field.fieldId}`}
                                    className={cx(
                                        'FieldSelector__content__fields__item',
                                        { 'FieldSelector__content__fields__item-selected': this.isSelected(field) },
                                    )}
                                    onMouseDown={multiple ? this.handleFieldChange: undefined}
                                    onClick={multiple ? undefined : this.handleFieldChange}
                                >
                                    <img src={field.imageURL} />
                                    <Ellipsis text={field.fieldName} width="calc(100% - 20px)" />
                                </button>
                            ))}
                        </div>
                    </Flex>
                </div>
                <ModalFooter>
                    <Button 
                        disabled={submitting}
                        id={getId?.cancelBtn || "btn-field-select-cancel"} 
                        onClick={close}
                    >
                        {I18n.t('Cancel')}
                    </Button>
                    <Button 
                        id={getId?.submitBtn || "btn-field-select-add"} 
                        disabled={!selectedFields.length || submitting} 
                        className="btn-primary" 
                        onClick={this.onChange}
                    >
                        {I18n.t('Add')}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}